import { Transformable, normalizeTransformable } from '../utils/Transformable';
import { parseIgnoreAsset } from '../utils/parseIgnoreAsset';
import { parseCORS } from '../utils/parseCORS';
import { toAbsolutePath } from '../utils/url';

import { AssetsMap, fillAssetsMap, AssetsModule } from '../AssetsMap';
import type { AppPlugin } from '../Plugin';
import { ScriptNode } from '../ScriptNode';
import { StyleNode } from '../StyleNode';
import { Page } from '../Page';

declare module '../Def' {
    interface AppPluginOptions {
        assetsMap?: Transformable<AssetsMap>;
        preferCORS?: PreferCORS;
        ignoreAsset?: IgnoreAsset;
    }
}

export function createLoadFromAssetsMapPlugin<AdditionalOptions, CustomProps>(): AppPlugin<
    AdditionalOptions,
    CustomProps
> {
    return ({ app }) => {
        let page: Page<CustomProps> | null = null;
        const { options, lifecycle } = app;

        if (!('assetsMap' in options)) {
            return;
        }

        lifecycle.hooks.load.tapPromise('LoadFromAssetsMapPlugin', () => {
            const crossOrigin = parseCORS(options.preferCORS);
            const ignoreAsset = parseIgnoreAsset(options.ignoreAsset);

            return Promise.resolve()
                .then(() => {
                    if (!options.assetsMap) {
                        return Promise.reject(Error(`assetsMap cannot be "${options.assetsMap}"`));
                    }

                    return normalizeTransformable<AssetsMap>(options.assetsMap);
                })
                .then(assetsMap => fillAssetsMap(assetsMap))
                .then(fullAssetsMap => {
                    const cssAssets = fullAssetsMap.initial.css;
                    const jsAssets = fullAssetsMap.initial.js;

                    const scripts: ScriptNode[] = [];

                    const styles: StyleNode[] = cssAssets.map(css => new StyleNode({ href: css, crossOrigin }));

                    if (fullAssetsMap.module === AssetsModule.ESM) {
                        // Keep the last one in ESM mode.
                        scripts.push(
                            new ScriptNode({
                                src: toAbsolutePath(jsAssets.pop()),
                                type: 'module',
                                crossOrigin,
                            })
                        );
                    } else {
                        // Keep all.
                        scripts.push(
                            ...jsAssets.map(
                                js =>
                                    new ScriptNode({
                                        src: toAbsolutePath(js),
                                        crossOrigin,
                                    })
                            )
                        );
                    }

                    page = new Page<CustomProps>({
                        scripts: scripts.filter(s => (s.src ? !ignoreAsset(s.src) : true)),
                        styles: styles.filter(s => (s.href ? !ignoreAsset(s.href) : true)),
                        name: options.name,
                    });

                    return page.resolveLifecycle(
                        !options.loose,
                        options.fetchResourceOptions,
                        options.envWithinLoading
                    );
                });
        });

        lifecycle.hooks.beforemount.tapPromise('LoadFromAssetsMapPlugin', () => {
            return Promise.resolve(page?.show());
        });

        lifecycle.hooks.afterunmount.tapPromise('LoadFromAssetsMapPlugin', () => {
            return Promise.resolve(page?.hide());
        });

        app.hooks.afterunload.tapPromise('LoadFromAssetsMapPlugin', () => {
            return Promise.resolve(page?.destroy());
        });
    };
}
